import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {defer, interval, Subject} from 'rxjs';
import {concatMap, last, takeWhile} from 'rxjs/operators';
import {ReportAdminService} from '../../services/api/report-admin.service';
import {AdminNavigationService} from '../../services/admin-navigation.service';
import {AdminReportGroupFilter} from '../../viewmodels/adminReportGroupFilter';
import {RecentAccountActivityReportDataViewModel} from '../../viewmodels/recentAccountActivityReportDataViewModel';
import {DateFormat, Dates, LoadingState, Title} from 'common';
import {BaseComponent} from '../base.component';

@Component({
    selector: 'app-report',
    templateUrl: './recent-account-activity-report-table-view.component.html',
    styleUrls: ['./recent-account-activity-report-table-view.component.scss']
})

export class RecentAccountActivityReportTableViewComponent extends BaseComponent implements OnInit {
    public readonly dataSource: MatTableDataSource<RecentAccountActivityReportDataViewModel>;

    public columns: Map<string, Title>;
    public loadingState: LoadingState;
    public loadingProgress: number;
    public loadingStatus: string;
    public dateCreated: string;
    public fileName: string;
    public viewModel: RecentAccountActivityReportDataViewModel;
    private reportFinished: Subject<void>;
    public filter: AdminReportGroupFilter;
    public truncateColumns = ['serialCodes', 'fullName', 'specialty', 'subscriptionAuthor'];

    @ViewChild(MatSort)
    public sort: MatSort;

    constructor(private route: ActivatedRoute,
                private reportAdminService: ReportAdminService,
                private adminNavigation: AdminNavigationService) {
        super();
        this.dataSource = new MatTableDataSource([]);

        this.columns = new Map<string, string>();
        this.columns.set('position', 'No');
        this.columns.set('fullName', 'Name');
        this.columns.set('email', 'Mail');
        this.columns.set('phone', 'Phone');
        this.columns.set('specialty', 'Specialty');
        this.columns.set('createdOnDate', 'Created');
        this.columns.set('country', 'Country');
        this.columns.set('zipCode', 'PostalCode');
        this.columns.set('subscription', 'Subscription');
        this.columns.set('subscriptionPeriodUnit', 'subscription Period');
        this.columns.set('subscriptionSource', 'Subscription Source');
        this.columns.set('subscriptionAuthor', 'Subscription Author');
        this.columns.set('nextPaymentDate', 'Next Payment');
        this.columns.set('lastPaymentDate', 'Last Payment');
        this.columns.set('expirationDate', 'Expiration Date');
        this.columns.set('totalNumberOfPayments', 'Total number of payments');
        this.columns.set('totalPaymentsAmount', 'Total Payments Amount');
        this.columns.set('sessionsOneWeek', 'Sessions 1 Week');
        this.columns.set('sessionsOneMonth', 'Sessions 1 Month');
        this.columns.set('sessionsThreeMonths', 'Sessions 3 Months');
        this.columns.set('sessionsYear', 'Sessions 12 Months');
        this.columns.set('totalSessions', 'Total Sessions');
        this.columns.set('lastUsedDate', 'Last Used');
        this.columns.set('serialCodes', 'Serial Codes');
        this.columns.set('grip', 'Grip');
        this.columns.set('muscle_tester', 'Muscle');
        this.columns.set('plates', 'Plates');
        this.columns.set('sens', 'Sens');
        this.columns.set('bubble', 'Bubble');
        this.columns.set('link', 'Link');
        this.columns.set('hexas', 'Deltas');
        this.columns.set('emg', 'Myo');
        this.columns.set('kpower', 'K Power');
        this.columns.set('deltas_3d', '3D Deltas');
        this.columns.set('code', 'Account Code');
        this.columns.set('notes', 'Subscription Notes');
        this.columns.set('accountRole', 'Account Role');

        this.viewModel = new RecentAccountActivityReportDataViewModel();
        this.reportFinished = new Subject<void>();

        this.filter = new AdminReportGroupFilter();
        this.filter.supportsQuery = true;
        this.filter.supportsSubscriptionPlanTypes = true;
        this.filter.supportsSubscriptionPeriodUnits = true;
        this.filter.supportsSubscriptionSources = true;
    }

    ngOnInit(): void {
        this.analyticsSwitchPage('Admin-RecentAccountActivityReport', '', '/admin/recentAccountActivityReport');

        this.loadingState = LoadingState.LOADING;

        this.runReportUntilCompleted();
    }

    /** Periodically request the status of the report and if not completed show the results in the loadingStatus, loadingProgress variables. <br>
     *  When completed show the data.<br><br>
     *  Due to the recent account activity large response size, we need to wait until the request is processed and then retry the request.<br>
     *  If the request is being processed and data bytes are being transferred, then do not re-request.
     */
    private runReportUntilCompleted(): void {
        const intervalSeconds = 5; // Interval in seconds

        interval(intervalSeconds * 1000)
            .pipe(
                concatMap(() => defer(this.reportAdminService.getRecentAccountActivity)),
                takeWhile(response => {
                    // Check the response and append the progress and continue making requests if condition not met.
                    this.loadingProgress = response.reportStatus.progress;
                    this.loadingStatus = response.reportStatus.progress.toString();
                    return response.reportStatus.progress !== 100;
                }, true),
                last()
            )
            .subscribe(
                (response) => {
                    // Handle the response
                    const status = response?.reportStatus;

                    if (status !== undefined && status !== null &&
                            status.dateCreated !== 0 && status.progress === 100) {

                        // display results
                        this.dataSource.data = RecentAccountActivityReportDataViewModel.from(response.rows);
                        const date = new Date(status.dateCreated);
                        this.dateCreated = Dates.format(date, DateFormat.DATE_TIME_LONG);
                        this.fileName = `account_activity_report_${Dates.format(date, DateFormat.DATE_TIME_CONDENSED)}`;

                        this.loadingState = LoadingState.LOADED;
                    }
                },
                error => {
                    this.errorHandler.handle(`Could not create report. ${error.statusText} (${error.status}`);
                    this.notification.info(`Could not create report. ${error.statusText} (${error.status}`);
                    this.loadingState = LoadingState.ERROR;
                }
            );
    }
}
